import { useState, useEffect, useMemo } from 'react';
import cookie from '@boiseitguru/cookie-cutter';
import { uuPdpService } from '../services';
import { FALLBACK_CHECKBOX_CONTENT, FALLBACK_POPUP_CONTENT } from '../constants/consent';

type HooksUUPDPType = {
  serverSideUUPDPCheckboxContent?: string;
};

const useUuPdp = ({ serverSideUUPDPCheckboxContent = null }: HooksUUPDPType) => {
  const [uuCheckboxContent, setUUCheckboxContent] = useState(serverSideUUPDPCheckboxContent ?? '');
  const [uuPopupContent, setUUPopupContent] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);

  const fallbackCheckboxContent = FALLBACK_CHECKBOX_CONTENT;
  const fallbackPopupContent = FALLBACK_POPUP_CONTENT;

  const fetchUUPdpData = async (fetchCheckbox = true, fetchPopup = true) => {
    try {
      // If we don't need to fetch anything, return early
      if (!fetchCheckbox && !fetchPopup) {
        return { checkboxContent: fallbackCheckboxContent, popupContent: fallbackPopupContent };
      }

      const { data } = await uuPdpService.getPdpAll();

      if (!Array.isArray(data?.body)) {
        return { checkboxContent: fallbackCheckboxContent, popupContent: fallbackPopupContent };
      }

      const checkboxConsent = fetchCheckbox
        ? data.body.find(
            (item) => item?.consentSourceCode === 4 && item?.consentSourceDetailCode === 4
          )?.bodyMessage || fallbackCheckboxContent
        : fallbackCheckboxContent;

      const popupConsent = fetchPopup
        ? data.body.find(
            (item) => item?.consentSourceCode === 4 && item?.consentSourceDetailCode === 5
          )?.bodyMessage || fallbackPopupContent
        : fallbackPopupContent;

      return { checkboxContent: checkboxConsent, popupContent: popupConsent };
    } catch (error) {
      console.log({ error });
      return { checkboxContent: fallbackCheckboxContent, popupContent: fallbackPopupContent };
    }
  };

  const loadUUPDPData = async () => {
    setIsLoadingData(true);

    try {
      // Determine what we need to fetch
      const needCheckboxContent =
        !serverSideUUPDPCheckboxContent && !cookie.get('UU_PDP_CHECKBOX_CONTENT');
      const needPopupContent = !cookie.get('UU_PDP_POPUP_CONTENT');

      // For checkbox content - try to get from cache if no server-side content
      if (!serverSideUUPDPCheckboxContent) {
        const checkboxCache = cookie.get('UU_PDP_CHECKBOX_CONTENT');
        if (checkboxCache) {
          const decoded = Buffer.from(decodeURIComponent(checkboxCache), 'base64').toString();
          setUUCheckboxContent(decoded);
        }
      }

      // For popup content - try to get from cache
      const popupCache = cookie.get('UU_PDP_POPUP_CONTENT');
      if (popupCache) {
        const decoded = Buffer.from(decodeURIComponent(popupCache), 'base64').toString();
        setUUPopupContent(decoded);
      }

      // Only fetch if either content is needed
      if (needCheckboxContent || needPopupContent) {
        // Only fetch what we need
        const { checkboxContent, popupContent } = await fetchUUPdpData(
          needCheckboxContent,
          needPopupContent
        );

        if (needCheckboxContent) {
          setUUCheckboxContent(checkboxContent);

          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          cookie.set('UU_PDP_CHECKBOX_CONTENT', Buffer.from(checkboxContent).toString('base64'), {
            expires: nextWeek,
            path: '/',
          });
        }

        if (needPopupContent) {
          setUUPopupContent(popupContent);

          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          cookie.set('UU_PDP_POPUP_CONTENT', Buffer.from(popupContent).toString('base64'), {
            expires: nextWeek,
            path: '/',
          });
        }
      }
    } catch (error) {
      console.log({ error });
      if (!serverSideUUPDPCheckboxContent) {
        setUUCheckboxContent(fallbackCheckboxContent);
      }
      setUUPopupContent(fallbackPopupContent);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    loadUUPDPData();
  }, [serverSideUUPDPCheckboxContent]);

  const handleSubmitPdp = async ({
    consentStatusId,
    phoneNumber,
    customerName,
  }: {
    consentStatusId: number;
    customerName?: string;
    phoneNumber?: string;
  }) => {
    try {
      await uuPdpService.submitUuPdp({
        consentStatusId,
        phoneNumber,
        customerName,
      });
    } catch (error) {
      console.log(error);
    }
  };

  return {
    uuCheckboxContent,
    uuPopupContent,
    isLoading: isLoadingData,
    handleSubmitPdp,
  };
};

export default useUuPdp;
